import React, { useState, useEffect } from 'react';
import './ManageTeacherAccount.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMobileAlt,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaLock,
  FaSave,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle
} from 'react-icons/fa';

const ManageTeacherAccount = ({ user }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Personal Info State
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    phone: '',
    mobile: '',
    dateOfBirth: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setPersonalInfo({
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        email: user.email || '',
        department: user.profile?.department || '',
        phone: user.profile?.phone || '',
        mobile: user.profile?.mobile || '',
        dateOfBirth: user.profile?.dateOfBirth ? 
          new Date(user.profile.dateOfBirth).toISOString().split('T')[0] : '',
        street: user.profile?.permanentAddress?.street || '',
        city: user.profile?.permanentAddress?.city || '',
        state: user.profile?.permanentAddress?.state || '',
        postalCode: user.profile?.permanentAddress?.postalCode || '',
        country: user.profile?.permanentAddress?.country || ''
      });
    }
  }, [user]);

  // Validate password strength
  useEffect(() => {
    const password = passwordData.newPassword;
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [passwordData.newPassword]);

  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePersonalInfoSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const token = localStorage.getItem('token');
            // Get user ID - ensure it's a string
            const userId = (user._id || user.id || user.userId).toString();

            const response = await fetch(`${collegeConfig.api.baseURL}/teachers/${userId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email: personalInfo.email,
                firstName: personalInfo.firstName,
                lastName: personalInfo.lastName,
                department: personalInfo.department,
                phone: personalInfo.phone,
                mobile: personalInfo.mobile,
                dateOfBirth: personalInfo.dateOfBirth || null,
                permanentAddress: {
                street: personalInfo.street,
                city: personalInfo.city,
                state: personalInfo.state,
                postalCode: personalInfo.postalCode,
                country: personalInfo.country
                }
            })
            });

            const data = await response.json();

            if (data.success) {
            setMessage({ type: 'success', text: 'Personal information updated successfully!' });
            
            // Update localStorage with new user data
            const updatedUser = { 
                ...user, 
                email: personalInfo.email,
                profile: {
                ...user.profile,
                firstName: personalInfo.firstName,
                lastName: personalInfo.lastName,
                department: personalInfo.department,
                phone: personalInfo.phone,
                mobile: personalInfo.mobile,
                dateOfBirth: personalInfo.dateOfBirth,
                permanentAddress: {
                    street: personalInfo.street,
                    city: personalInfo.city,
                    state: personalInfo.state,
                    postalCode: personalInfo.postalCode,
                    country: personalInfo.country
                }
                }
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            // Reload page after 1.5 seconds to reflect changes
            setTimeout(() => {
                window.location.reload();
            }, 1500);
            } else {
            setMessage({ type: 'error', text: data.message || 'Failed to update information' });
            }
        } catch (error) {
            console.error('Error updating personal info:', error);
            setMessage({ type: 'error', text: 'An error occurred while updating information' });
        } finally {
            setLoading(false);
        }
        };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation checks
    if (passwordData.newPassword !== passwordData.confirmPassword) {
        setMessage({ type: 'error', text: 'New passwords do not match!' });
        setLoading(false);
        return;
    }

    const allValid = Object.values(passwordValidation).every(v => v);
    if (!allValid) {
        setMessage({ type: 'error', text: 'Password does not meet security requirements!' });
        setLoading(false);
        return;
    }

    try {
        const token = localStorage.getItem('token');
        // Get user ID - ensure it's a string
        const userId = (user._id || user.id || user.userId).toString();

        // Use the teacher route endpoint with the user ID
        const response = await fetch(`${collegeConfig.api.baseURL}/teachers/${userId}/change-password`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
        })
        });

        const data = await response.json();

        if (data.success) {
        setMessage({ type: 'success', text: 'Password updated successfully! Logging out...' });
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        
        // Logout after 2 seconds
        setTimeout(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login'; // Redirect to login page
        }, 2000);
        } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update password' });
        }
    } catch (error) {
        console.error('Error updating password:', error);
        setMessage({ type: 'error', text: 'An error occurred while updating password' });
    } finally {
        setLoading(false);
    }
    };

  return (
    <div className="manage-account-container">
      <div className="account-header">
        <h2>Manage My Account</h2>
        <p>Update your personal information and security settings</p>
      </div>

      {/* Tab Navigation */}
      <div className="account-tabs">
        <button
          className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('personal');
            setMessage({ type: '', text: '' });
          }}
        >
          <FaUser /> Personal Information
        </button>
        <button
          className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('security');
            setMessage({ type: '', text: '' });
          }}
        >
          <FaLock /> Security & Password
        </button>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`message-alert ${message.type}`}>
          {message.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Personal Information Tab */}
      {activeTab === 'personal' && (
        <form onSubmit={handlePersonalInfoSubmit} className="account-form">
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <FaUser /> First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={personalInfo.firstName}
                  onChange={handlePersonalInfoChange}
                  required
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaUser /> Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={personalInfo.lastName}
                  onChange={handlePersonalInfoChange}
                  required
                  placeholder="Enter last name"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaEnvelope /> Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={personalInfo.email}
                  onChange={handlePersonalInfoChange}
                  required
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaUser /> Department
                </label>
                <input
                  type="text"
                  name="department"
                  value={personalInfo.department}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter department"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaPhone /> Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={personalInfo.phone}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaMobileAlt /> Mobile
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={personalInfo.mobile}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter mobile number"
                />
              </div>

              <div className="form-group">
                <label>
                  <FaCalendarAlt /> Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={personalInfo.dateOfBirth}
                  onChange={handlePersonalInfoChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Permanent Address</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>
                  <FaMapMarkerAlt /> Street Address
                </label>
                <input
                  type="text"
                  name="street"
                  value={personalInfo.street}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter street address"
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={personalInfo.city}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter city"
                />
              </div>

              <div className="form-group">
                <label>State/Province</label>
                <input
                  type="text"
                  name="state"
                  value={personalInfo.state}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter state"
                />
              </div>

              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  name="postalCode"
                  value={personalInfo.postalCode}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter postal code"
                />
              </div>

              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={personalInfo.country}
                  onChange={handlePersonalInfoChange}
                  placeholder="Enter country"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FaSave /> Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Security & Password Tab */}
      {activeTab === 'security' && (
        <form onSubmit={handlePasswordSubmit} className="account-form">
          <div className="form-section">
            <h3 className="section-title">Change Password</h3>
            <p className="section-description">
              Ensure your account is using a strong password to stay secure.
            </p>

            <div className="form-grid">
              <div className="form-group full-width">
                <label>
                  <FaLock /> Current Password *
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group full-width">
                <label>
                  <FaLock /> New Password *
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group full-width">
                <label>
                  <FaLock /> Confirm New Password *
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Requirements */}
            {passwordData.newPassword && (
              <div className="password-requirements">
                <h4>Password Requirements:</h4>
                <div className="requirement-list">
                  <div className={`requirement ${passwordValidation.minLength ? 'met' : ''}`}>
                    {passwordValidation.minLength ? <FaCheckCircle /> : <FaTimesCircle />}
                    At least 8 characters long
                  </div>
                  <div className={`requirement ${passwordValidation.hasUpperCase ? 'met' : ''}`}>
                    {passwordValidation.hasUpperCase ? <FaCheckCircle /> : <FaTimesCircle />}
                    Contains uppercase letter (A-Z)
                  </div>
                  <div className={`requirement ${passwordValidation.hasLowerCase ? 'met' : ''}`}>
                    {passwordValidation.hasLowerCase ? <FaCheckCircle /> : <FaTimesCircle />}
                    Contains lowercase letter (a-z)
                  </div>
                  <div className={`requirement ${passwordValidation.hasNumber ? 'met' : ''}`}>
                    {passwordValidation.hasNumber ? <FaCheckCircle /> : <FaTimesCircle />}
                    Contains number (0-9)
                  </div>
                  <div className={`requirement ${passwordValidation.hasSpecialChar ? 'met' : ''}`}>
                    {passwordValidation.hasSpecialChar ? <FaCheckCircle /> : <FaTimesCircle />}
                    Contains special character (!@#$%^&*)
                  </div>
                </div>
              </div>
            )}

            {/* Password Mismatch Warning */}
            {passwordData.newPassword && passwordData.confirmPassword && 
             passwordData.newPassword !== passwordData.confirmPassword && (
              <div className="password-mismatch">
                <FaExclamationTriangle />
                <span>Passwords do not match!</span>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="save-button" 
              disabled={loading || !Object.values(passwordValidation).every(v => v)}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Updating...
                </>
              ) : (
                <>
                  <FaSave /> Update Password
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ManageTeacherAccount;